import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  CircularProgress,
  Alert,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Link,
  Collapse,
  ListItemButton,
  Tab,
  Tabs,
  Chip,
} from '../ui';
import { CheckCircle as CheckCircleIcon, XCircle as CancelIcon, Info as InfoIcon, Copy as ContentCopyIcon, Settings as SettingsIcon, Download as DownloadIcon, Filter as FilterAltIcon, Shield as RatingIcon } from '../../lib/icons';
import useMediaQuery from '../../hooks/useMediaQuery';
import { useConfig } from '../../hooks/useConfig';
import { useSubfolders } from '../../hooks/useSubfolders';
import { SubfolderAutocomplete } from '../shared/SubfolderAutocomplete';
import { ResolutionSelect } from '../shared/ResolutionSelect';
import { AudioFormatSelect } from '../shared/AudioFormatSelect';
import { RatingSelect } from '../shared/RatingSelect';
import { RATING_OPTIONS } from '../../utils/ratings';
import RatingBadge from '../shared/RatingBadge';
import TabsEditor, { TabsEditorRefreshResult } from './components/TabsEditor';

const M3U_SORT_ORDERS = ['oldest_first', 'newest_first'] as const;
type M3uSortOrder = (typeof M3U_SORT_ORDERS)[number];

const toM3uSortOrder = (value: unknown): M3uSortOrder =>
  M3U_SORT_ORDERS.includes(value as M3uSortOrder) ? (value as M3uSortOrder) : 'oldest_first';

interface ChannelSettings {
  sub_folder: string | null;
  video_quality: string | null;
  min_duration: number | null;
  max_duration: number | null;
  title_filter_regex: string | null;
  default_rating: string | null;
  auto_download_enabled_tabs: string | null;
  audio_format: string | null;
  skip_video_folder: boolean | null;
  hidden_tabs: string[];
  m3u_enabled: boolean;
  m3u_sort_order: M3uSortOrder;
}

interface ChannelTabsState {
  detectedTabs: string[];
  availableTabs: string[];
}

interface FilterPreviewVideo {
  video_id: string;
  title: string;
  upload_date: string;
  matches: boolean;
}

interface FilterPreviewResult {
  videos: FilterPreviewVideo[];
  totalCount: number;
  matchCount: number;
}

interface ChannelSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  channelId: string;
  channelName: string;
  token: string | null;
  onSettingsSaved?: (settings: ChannelSettings & ChannelTabsState) => void;
}

const TAB_TO_MEDIA_TYPE: Record<string, string> = {
  videos: 'video',
  shorts: 'short',
  streams: 'livestream',
};

const MEDIA_TYPE_LABEL: Record<string, string> = {
  video: 'New Videos',
  short: 'New Shorts',
  livestream: 'New Live/Streams',
};

const MEDIA_TYPE_ORDER: string[] = ['video', 'short', 'livestream'];

const regexExamples = [
  {
    label: 'Exclude videos containing a word (case-insensitive)',
    pattern: '(?i)^(?!.*roblox).*',
    description: 'Excludes videos with "roblox" in the title'
  },
  {
    label: 'Exclude videos containing multiple words',
    pattern: '(?i)^(?!.*(roblox|minecraft)).*',
    description: 'Excludes videos with "roblox" OR "minecraft"'
  },
  {
    label: 'Include only videos matching specific phrases',
    pattern: '(?i)(Official Trailer|New Trailer)',
    description: 'Only matches videos containing these phrases'
  }
];

function ChannelSettingsDialog({
  open,
  onClose,
  channelId,
  token,
  onSettingsSaved
}: ChannelSettingsDialogProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [activeSection, setActiveSection] = useState('general');

  const [settings, setSettings] = useState<ChannelSettings>({
    sub_folder: null,
    video_quality: null,
    min_duration: null,
    max_duration: null,
    title_filter_regex: null,
    default_rating: null,
    audio_format: null,
    auto_download_enabled_tabs: null,
    skip_video_folder: null,
    hidden_tabs: [],
    m3u_enabled: false,
    m3u_sort_order: 'oldest_first',
  });
  const [originalSettings, setOriginalSettings] = useState<ChannelSettings>({
    sub_folder: null,
    video_quality: null,
    min_duration: null,
    max_duration: null,
    title_filter_regex: null,
    default_rating: null,
    audio_format: null,
    auto_download_enabled_tabs: null,
    skip_video_folder: null,
    hidden_tabs: [],
    m3u_enabled: false,
    m3u_sort_order: 'oldest_first',
  });
  const [detectedTabs, setDetectedTabs] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Use config hook to get global quality setting
  const { config, refetch: refetchConfig } = useConfig(token);
  const globalQuality = config.preferredResolution || '1080';

  const { subfolders, createSubfolder } = useSubfolders(token);

  // Duration input state (in minutes for UI convenience)
  const [minDurationMinutes, setMinDurationMinutes] = useState<string>('');
  const [maxDurationMinutes, setMaxDurationMinutes] = useState<string>('');

  // Preview state
  const [previewResult, setPreviewResult] = useState<FilterPreviewResult | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Regex examples collapsible state
  const [showRegexExamples, setShowRegexExamples] = useState(false);

  const effectiveQualityDisplay = settings.video_quality
    ? `${settings.video_quality}p (channel)`
    : `${globalQuality}p (global)`;

  const sections = [
    { id: 'general', label: 'General', icon: <SettingsIcon size={18} /> },
    { id: 'filters', label: 'Filters', icon: <FilterAltIcon size={18} /> },
    { id: 'ratings', label: 'Ratings', icon: <RatingIcon size={18} /> }
  ];

  useEffect(() => {
    if (open) {
      // Ensure we have the latest saved global defaults whenever dialog opens
      refetchConfig().catch((err) => console.error('Failed to refresh config for channel settings dialog:', err));
    }
  }, [open, refetchConfig]);

  useEffect(() => {
    if (!open) {
      // Reset state when dialog closes
      setSuccess(false);
      setError(null);
      return;
    }

    setActiveSection('general');

    // Load all data when dialog opens
    const loadAllData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Load settings
        const settingsResponse = await fetch(`/api/channels/${channelId}/settings`, {
          headers: {
            'x-access-token': token || ''
          }
        });

        if (!settingsResponse.ok) {
          throw new Error('Failed to load channel settings');
        }

        const settingsData = await settingsResponse.json();
        const loadedSettings: ChannelSettings = {
          sub_folder: settingsData.sub_folder || null,
          video_quality: settingsData.video_quality || null,
          min_duration: settingsData.min_duration || null,
          max_duration: settingsData.max_duration || null,
          title_filter_regex: settingsData.title_filter_regex || null,
          auto_download_enabled_tabs: settingsData.auto_download_enabled_tabs ?? 'video',
          audio_format: settingsData.audio_format || null,
          default_rating: Object.prototype.hasOwnProperty.call(settingsData, 'default_rating')
            ? settingsData.default_rating
            : null,
          skip_video_folder: Object.prototype.hasOwnProperty.call(settingsData, 'skip_video_folder')
            ? settingsData.skip_video_folder
            : null,
          hidden_tabs: Array.isArray(settingsData.hidden_tabs) ? settingsData.hidden_tabs : [],
          m3u_enabled: settingsData.m3u_enabled === true,
          m3u_sort_order: toM3uSortOrder(settingsData.m3u_sort_order),
        };
        setSettings(loadedSettings);
        setOriginalSettings(loadedSettings);
        setDetectedTabs(Array.isArray(settingsData.detected_tabs) ? settingsData.detected_tabs : []);

        // Convert seconds to minutes for UI
        if (settingsData.min_duration) {
          setMinDurationMinutes(String(Math.floor(settingsData.min_duration / 60)));
        }
        if (settingsData.max_duration) {
          setMaxDurationMinutes(String(Math.floor(settingsData.max_duration / 60)));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [open, channelId, token]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch(`/api/channels/${channelId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': token || ''
        },
        body: JSON.stringify({
          sub_folder: settings.sub_folder || null,
          video_quality: settings.video_quality || null,
          min_duration: settings.min_duration,
          max_duration: settings.max_duration,
          title_filter_regex: settings.title_filter_regex || null,
          default_rating: settings.default_rating || null,
          audio_format: settings.audio_format || null,
          auto_download_enabled_tabs: settings.auto_download_enabled_tabs,
          skip_video_folder: settings.skip_video_folder,
          hidden_tabs: settings.hidden_tabs,
          m3u_enabled: settings.m3u_enabled,
          m3u_sort_order: settings.m3u_sort_order
        })
      });

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('Cannot change subfolder while downloads are in progress for this channel. Please wait for downloads to complete.');
        }

        let errorMessage = 'Failed to update settings';
        try {
          const data = await response.json();
          errorMessage = data.error || errorMessage;
        } catch (parseError) {
          // If JSON parsing fails, use generic error with status
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      const resultHiddenTabs: string[] = Array.isArray(result?.settings?.hidden_tabs)
        ? result.settings.hidden_tabs
        : settings.hidden_tabs;
      const resultDetectedTabs: string[] = Array.isArray(result?.settings?.detected_tabs)
        ? result.settings.detected_tabs
        : detectedTabs;
      const resultAvailableTabs: string[] = Array.isArray(result?.settings?.available_tabs)
        ? result.settings.available_tabs
        : resultDetectedTabs.filter((tab) => !resultHiddenTabs.includes(tab));

      const updatedSettings: ChannelSettings = {
        sub_folder: result?.settings?.sub_folder ?? settings.sub_folder ?? null,
        video_quality: result?.settings?.video_quality ?? settings.video_quality ?? null,
        min_duration: result?.settings?.min_duration ?? settings.min_duration ?? null,
        max_duration: result?.settings?.max_duration ?? settings.max_duration ?? null,
        title_filter_regex: result?.settings?.title_filter_regex ?? settings.title_filter_regex ?? null,
        audio_format: result?.settings?.audio_format ?? settings.audio_format ?? null,
        default_rating: result?.settings && Object.prototype.hasOwnProperty.call(result.settings, 'default_rating')
          ? result.settings.default_rating
          : settings.default_rating ?? null,
        auto_download_enabled_tabs: result?.settings?.auto_download_enabled_tabs ?? settings.auto_download_enabled_tabs ?? null,
        skip_video_folder: result?.settings && Object.prototype.hasOwnProperty.call(result.settings, 'skip_video_folder')
          ? result.settings.skip_video_folder
          : settings.skip_video_folder ?? null,
        hidden_tabs: resultHiddenTabs,
        m3u_enabled: result?.settings?.m3u_enabled ?? settings.m3u_enabled,
        m3u_sort_order: toM3uSortOrder(result?.settings?.m3u_sort_order ?? settings.m3u_sort_order),
      };

      setSettings(updatedSettings);
      setOriginalSettings(updatedSettings);
      setDetectedTabs(resultDetectedTabs);
      setSuccess(true);

      if (onSettingsSaved) {
        onSettingsSaved({
          ...updatedSettings,
          detectedTabs: resultDetectedTabs,
          availableTabs: resultAvailableTabs,
        });
      }

      // Show success message briefly then close
      setTimeout(() => {
        onClose();
      }, 1500);

      // If folder was moved, show additional info
      if (result.folderMoved && result.moveResult) {
        console.log('Channel folder moved:', result.moveResult);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save settings';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSettings(originalSettings);
    onClose();
  };

  const hasChanges = () => {
    const tabsChanged =
      settings.hidden_tabs.length !== originalSettings.hidden_tabs.length ||
      settings.hidden_tabs.some((tab) => !originalSettings.hidden_tabs.includes(tab));
    return settings.sub_folder !== originalSettings.sub_folder ||
           settings.video_quality !== originalSettings.video_quality ||
           settings.min_duration !== originalSettings.min_duration ||
           settings.max_duration !== originalSettings.max_duration ||
           settings.title_filter_regex !== originalSettings.title_filter_regex ||
           settings.audio_format !== originalSettings.audio_format ||
           settings.default_rating !== originalSettings.default_rating ||
           settings.auto_download_enabled_tabs !== originalSettings.auto_download_enabled_tabs ||
           settings.skip_video_folder !== originalSettings.skip_video_folder ||
           settings.m3u_enabled !== originalSettings.m3u_enabled ||
           settings.m3u_sort_order !== originalSettings.m3u_sort_order ||
           tabsChanged;
  };

  const allTabsHidden = detectedTabs.length > 0 &&
    detectedTabs.every((tab) => settings.hidden_tabs.includes(tab));

  const handleHiddenTabsChange = (nextHidden: string[]) => {
    const hiddenMediaTypes = new Set(
      nextHidden.map((tab) => TAB_TO_MEDIA_TYPE[tab]).filter(Boolean)
    );
    setSettings((prev) => {
      const currentAuto = prev.auto_download_enabled_tabs
        ? prev.auto_download_enabled_tabs.split(',').map((t) => t.trim()).filter(Boolean)
        : [];
      const filteredAuto = currentAuto.filter((mt) => !hiddenMediaTypes.has(mt));
      return {
        ...prev,
        hidden_tabs: nextHidden,
        auto_download_enabled_tabs: filteredAuto.join(','),
      };
    });
  };

  const handleTabsRefresh = (result: TabsEditorRefreshResult) => {
    setDetectedTabs(result.detectedTabs);
    setSettings((prev) => ({
      ...prev,
      hidden_tabs: result.hiddenTabs,
      auto_download_enabled_tabs: result.autoDownloadEnabledTabs ?? prev.auto_download_enabled_tabs,
    }));
    setOriginalSettings((prev) => ({
      ...prev,
      hidden_tabs: result.hiddenTabs,
      auto_download_enabled_tabs: result.autoDownloadEnabledTabs ?? prev.auto_download_enabled_tabs,
    }));
  };

  const handlePreviewFilter = async () => {
    setLoadingPreview(true);
    setPreviewError(null);

    try {
      const regex = settings.title_filter_regex || '';
      const response = await fetch(
        `/api/channels/${channelId}/filter-preview?title_filter_regex=${encodeURIComponent(regex)}`,
        {
          headers: {
            'x-access-token': token || ''
          }
        }
      );

      let data;

      if (!response.ok) {
        try {
          data = await response.json();
        } catch (parseError) {
          // Ignore JSON parse failure; will fall back to default message
        }
        const serverMessage = data?.error;
        throw new Error(serverMessage && typeof serverMessage === 'string'
          ? serverMessage
          : 'Failed to load preview');
      }

      data = await response.json();
      setPreviewResult(data);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Failed to load preview');
      setPreviewResult(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDurationChange = (field: 'min' | 'max', value: string) => {
    // Allow empty or numeric values only
    if (value === '' || /^\d+$/.test(value)) {
      if (field === 'min') {
        setMinDurationMinutes(value);
        setSettings({
          ...settings,
          min_duration: value ? parseInt(value) * 60 : null
        });
      } else {
        setMaxDurationMinutes(value);
        setSettings({
          ...settings,
          max_duration: value ? parseInt(value) * 60 : null
        });
      }
    }
  };

  const handleCopyRegex = async (pattern: string) => {
    try {
      await navigator.clipboard.writeText(pattern);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const toggleAutoDownloadTab = (tab: string, enabled: boolean) => {
    const currentTabs = settings.auto_download_enabled_tabs ? settings.auto_download_enabled_tabs.split(',').map(t => t.trim()) : [];
    let newTabs;
    if (enabled) {
      if (!currentTabs.includes(tab)) {
        newTabs = [...currentTabs, tab].join(',');
      } else {
        newTabs = currentTabs.join(',');
      }
    } else {
      newTabs = currentTabs.filter(t => t !== tab).join(',');
    }
    setSettings({
      ...settings,
      auto_download_enabled_tabs: newTabs || ''
    });
  };

  const isTabEnabled = (tab: string) => {
    const currentTabs = settings.auto_download_enabled_tabs ? settings.auto_download_enabled_tabs.split(',').map(t => t.trim()) : [];
    return currentTabs.includes(tab);
  };

  const hiddenTabsSet = new Set(settings.hidden_tabs);
  const availableMediaTypes = MEDIA_TYPE_ORDER.filter((mediaType) => {
    const tabType = Object.keys(TAB_TO_MEDIA_TYPE).find((t) => TAB_TO_MEDIA_TYPE[t] === mediaType);
    return tabType !== undefined && detectedTabs.includes(tabType) && !hiddenTabsSet.has(tabType);
  });

  const renderSectionContent = (sectionId: string) => {
    switch (sectionId) {
      case 'general':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 16 : 24 }}>
            <TabsEditor
              channelId={channelId}
              token={token}
              detectedTabs={detectedTabs}
              hiddenTabs={settings.hidden_tabs}
              onHiddenTabsChange={handleHiddenTabsChange}
              onRefresh={handleTabsRefresh}
              disabled={saving}
            />

            <Divider />

            <div>
              <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                Auto Downloads
              </Typography>
              <Alert severity="info" style={{ marginBottom: 12 }}>
                <Typography variant="body2">
                  Enable these to automatically download new content from this channel during scheduled tasks.
                </Typography>
              </Alert>
              {availableMediaTypes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No detected tabs are available for auto-download. Enable a tab above first.
                </Typography>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
                  {availableMediaTypes.map((mediaType) => (
                    <FormControlLabel
                      key={mediaType}
                      control={
                        <Switch
                          checked={isTabEnabled(mediaType)}
                          onChange={(e) => toggleAutoDownloadTab(mediaType, e.target.checked)}
                        />
                      }
                      label={MEDIA_TYPE_LABEL[mediaType]}
                    />
                  ))}
                </div>
              )}
            </div>

            <Divider />

            <div>
              <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                Resolution Override
              </Typography>
              <ResolutionSelect
                label="Channel Video Quality Override"
                emptyLabel="Using Global Setting"
                value={settings.video_quality}
                onChange={(value) => setSettings({
                  ...settings,
                  video_quality: value
                })}
              />
              <Typography variant="caption" color="text.secondary" style={{ marginTop: 8, display: 'block' }}>
                Effective channel quality: {effectiveQualityDisplay}.
              </Typography>
              {(settings.video_quality === '1440' || settings.video_quality === '2160') && (
                <Typography variant="caption" color="warning.main" style={{ marginTop: 4, display: 'block' }}>
                  YouTube only serves H.264 MP4 up to 1080p. {settings.video_quality === '2160' ? '4K' : '1440p'} uses VP9/AV1 (remuxed into MP4); older Plex clients without native VP9/AV1 decode may transcode.
                </Typography>
              )}
            </div>

            <AudioFormatSelect
              className="mt-2"
              value={settings.audio_format}
              onChange={(value) => setSettings({
                ...settings,
                audio_format: value
              })}
              helperText={settings.audio_format ? 'MP3 files are saved at 192kbps in the same folder as videos.' : undefined}
            />

            <div className="mt-2">
              <FormControl fullWidth>
                <InputLabel id="video-file-structure-label">Video File Structure</InputLabel>
                <Select
                  labelId="video-file-structure-label"
                  value={settings.skip_video_folder === null ? 'default' : settings.skip_video_folder ? 'flat' : 'subfolders'}
                  onChange={(e: SelectChangeEvent<string>) => setSettings({
                    ...settings,
                    skip_video_folder: e.target.value === 'default' ? null : e.target.value === 'flat'
                  })}
                  label="Video File Structure"
                >
                  <MenuItem value="default">
                    Use global setting ({config.defaultSkipVideoFolder ? 'Flat' : 'Video subfolders'})
                  </MenuItem>
                  <MenuItem value="flat">Flat (no video subfolders)</MenuItem>
                  <MenuItem value="subfolders">Video subfolders</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" className="mt-1 mb-2 block">
                Flat saves video files directly in the channel folder instead of individual video subfolders. Only affects new downloads.
              </Typography>
            </div>

            <div className="mt-2">
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.m3u_enabled}
                    onChange={(e) => setSettings({
                      ...settings,
                      m3u_enabled: e.target.checked
                    })}
                  />
                }
                label="Generate channel playlist file (.m3u)"
              />
              <Typography variant="caption" color="text.secondary" className="mt-1 block">
                Writes a playlist of this channel&apos;s downloaded videos to the top of the
                channel folder. Jellyfin and Emby import it automatically; it updates after
                downloads and deletions, and refreshes nightly.
              </Typography>
              {settings.m3u_enabled && (
                <FormControl fullWidth className="mt-2">
                  <InputLabel id="m3u-sort-order-label">Playlist Order</InputLabel>
                  <Select
                    labelId="m3u-sort-order-label"
                    value={settings.m3u_sort_order}
                    onChange={(e: SelectChangeEvent<string>) => setSettings({
                      ...settings,
                      m3u_sort_order: toM3uSortOrder(e.target.value)
                    })}
                    label="Playlist Order"
                  >
                    <MenuItem value="oldest_first">Oldest first (chronological)</MenuItem>
                    <MenuItem value="newest_first">Newest first</MenuItem>
                  </Select>
                </FormControl>
              )}
            </div>

            <Alert severity="info" style={{ marginBottom: 16 }}>
              <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                Subfolder Organization
              </Typography>
              <Typography variant="body2">
                Use subfolders to keep channel downloads organized without changing your global storage path.
              </Typography>
            </Alert>

            <Divider />

            <div>
              <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                Subfolder
              </Typography>
              <SubfolderAutocomplete
                mode="channel"
                value={settings.sub_folder}
                onChange={(newValue) => {
                  setSettings({
                    ...settings,
                    sub_folder: newValue
                  });
                }}
                subfolders={subfolders}
                defaultSubfolderDisplay={config.defaultSubfolder || null}
                label="Subfolder"
                helperText="Choose where this channel's videos are saved"
                createSubfolder={createSubfolder}
              />
              <Alert severity="info" style={{ marginTop: 8 }}>
                <Typography variant="caption">
                  Subfolders are automatically prefixed with <code>__</code> on the filesystem.
                </Typography>
              </Alert>
              <Typography variant="caption" color="text.secondary" style={{ marginTop: 8, display: 'block' }}>
                Note: Changing the subfolder will move the channel&apos;s existing folder and files!
              </Typography>
            </div>
          </div>
        );
      case 'filters':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 16 : 24 }}>
            <div>
              <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                Duration Filters
              </Typography>
              <div style={{ display: 'flex', gap: 16, marginTop: 8, flexDirection: isMobile ? 'column' : 'row' }}>
                <TextField
                  label="Min Duration (mins)"
                  type="number"
                  value={minDurationMinutes}
                  onChange={(e) => handleDurationChange('min', e.target.value)}
                  placeholder="No minimum"
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Max Duration (mins)"
                  type="number"
                  value={maxDurationMinutes}
                  onChange={(e) => handleDurationChange('max', e.target.value)}
                  placeholder="No maximum"
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                  InputLabelProps={{ shrink: true }}
                />
              </div>
            </div>

            <Divider />

            <div>
              <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                Download Filters
              </Typography>
              <Typography variant="caption" color="text.secondary" style={{ display: 'block', marginBottom: 8 }}>
                Only download videos with titles matching regex pattern
              </Typography>
              <Typography variant="caption" color="text.secondary" style={{ display: 'block', marginBottom: 8 }}>
                These filters only apply to channel downloads. Manually selected videos will always download.
              </Typography>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8 }}>
                <TextField
                  label="Title Filter (Python Regex)"
                  value={settings.title_filter_regex || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    title_filter_regex: e.target.value || null
                  })}
                  placeholder="e.g., (?i)podcast|interview"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <a
                  href="https://docs.python.org/3/library/re.html#regular-expression-syntax"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Python regex documentation"
                  style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', color: 'var(--muted-foreground)' }}
                >
                  <InfoIcon size={16} />
                </a>
              </div>

              <div style={{ marginTop: 8 }}>
                <button
                  onClick={() => setShowRegexExamples(!showRegexExamples)}
                  style={{ color: 'var(--primary)', cursor: 'pointer', background: 'none', border: 'none', padding: 0, textDecoration: 'underline', fontSize: '0.75rem' }}
                >
                  {showRegexExamples ? 'Hide examples' : 'Show examples'}
                </button>
              </div>

              <Collapse in={showRegexExamples}>
                <div style={{ marginTop: 8, padding: 12, backgroundColor: 'var(--muted)', borderRadius: 4, border: '1px solid var(--border)' }}>
                  {regexExamples.map((example, index) => (
                    <div key={index} style={{ marginBottom: index < regexExamples.length - 1 ? 12 : 0 }}>
                      <Typography variant="caption" style={{ fontWeight: 600 }} gutterBottom>
                        {example.label}
                      </Typography>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: 'var(--card)', padding: '4px 8px', borderRadius: 4, marginBottom: 4 }}>
                        <Typography
                          variant="caption"
                          style={{ fontFamily: 'var(--font-body)', flex: 1, wordBreak: 'break-all' }}
                        >
                          {example.pattern}
                        </Typography>
                        <button
                          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: 2, color: 'var(--muted-foreground)' }}
                          onClick={() => handleCopyRegex(example.pattern)}
                        >
                          <ContentCopyIcon size={12} />
                        </button>
                      </div>
                      <Typography variant="caption" color="text.secondary" style={{ fontSize: '0.7rem' }}>
                        {example.description}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Collapse>
            </div>

            <div style={{ marginTop: 8 }}>
              <Button
                variant="outlined"
                onClick={handlePreviewFilter}
                disabled={loadingPreview || !settings.title_filter_regex}
                size="small"
              >
                {loadingPreview ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
                Preview Regex
              </Button>
              {previewResult && (
                <Typography variant="caption" color="text.secondary" style={{ marginLeft: 16 }}>
                  {previewResult.matchCount} of {previewResult.totalCount} recent videos match
                </Typography>
              )}
            </div>

            {previewError && (
              <Alert severity="error" onClose={() => setPreviewError(null)}>
                {previewError}
              </Alert>
            )}

            {previewResult && (
              <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 4 }}>
                <List dense>
                  {previewResult.videos.map((video) => (
                    <ListItem key={video.video_id}>
                      <ListItemIcon style={{ minWidth: 32 }}>
                        {video.matches ? (
                          <CheckCircleIcon size={16} style={{ color: 'var(--success)' }} />
                        ) : (
                          <CancelIcon size={16} style={{ color: 'var(--destructive)' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={video.title}
                        primaryTypographyProps={{
                          style: {
                            fontSize: '0.75rem',
                            opacity: video.matches ? 1 : 0.5,
                            textDecoration: video.matches ? 'none' : 'line-through'
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </div>
            )}
          </div>
        );
      case 'ratings': {
        const effectiveRatingLabel = settings.default_rating
          ? (RATING_OPTIONS.find((option) => option.value === settings.default_rating)?.label || settings.default_rating)
          : 'Global';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
              Content Ratings
            </Typography>
            <Alert severity="info">
              <Typography variant="body2">
                Set a default rating for videos from this channel when no rating metadata is available.
              </Typography>
            </Alert>
            <RatingSelect
              label="Default Rating"
              value={settings.default_rating}
              onChange={(value) => setSettings({
                ...settings,
                default_rating: value
              })}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                Effective default rating:
              </Typography>
              <RatingBadge 
                rating={effectiveRatingLabel} 
                size="small" 
              />
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel} 
      maxWidth="xl" 
      fullWidth
      className={isMobile ? 'w-[calc(100vw-24px)] max-w-none max-h-[calc(100dvh-24px)]' : 'max-h-[calc(100vh-120px)]'}
    >
      <DialogTitle style={{ paddingBottom: 8 }}>
        Channel Settings
      </DialogTitle>
      
      {isMobile ? (
        <div style={{ borderBottom: '1px solid var(--border)', padding: '0 8px' }}>
          <Tabs
            value={activeSection}
            onChange={(_, newValue) => setActiveSection(String(newValue))}
            variant="fullWidth"
          >
            {sections.map(section => (
              <Tab 
                key={section.id} 
                value={section.id} 
                label={section.label} 
                icon={section.icon}
                iconPosition="start"
                style={{ minHeight: 40, textTransform: 'none', fontSize: '0.72rem', paddingLeft: 8, paddingRight: 8 }}
              />
            ))}
          </Tabs>
        </div>
      ) : null}

      <DialogContent style={{ padding: 0, display: 'flex', minHeight: 0 }}>
        {!isMobile && (
          <div style={{ 
            width: 200, 
            borderRight: '1px solid var(--border)',
            backgroundColor: 'var(--muted)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <List style={{ paddingTop: 8 }}>
              {sections.map((section) => (
                <ListItemButton
                  key={section.id}
                  selected={activeSection === section.id}
                  onClick={() => setActiveSection(section.id)}
                  style={{
                    paddingTop: 12,
                    paddingBottom: 12,
                    borderLeft: activeSection === section.id ? '4px solid var(--primary)' : '4px solid transparent',
                  }}
                >
                  <ListItemIcon style={{ minWidth: 40, color: activeSection === section.id ? 'var(--primary)' : 'inherit' }}>
                    {section.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={section.label} 
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      style: {
                        fontWeight: activeSection === section.id ? 600 : 400,
                        color: activeSection === section.id ? 'var(--primary)' : 'inherit'
                      }
                    }} 
                  />
                </ListItemButton>
              ))}
            </List>
          </div>
        )}

        <div style={{ flex: 1, padding: isMobile ? 12 : 24, overflowY: 'auto', minHeight: 0 }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: 80, paddingBottom: 80 }}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {error && (
                <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" style={{ marginBottom: 16 }}>
                  Settings saved successfully!
                </Alert>
              )}

              {isMobile ? (
                renderSectionContent(activeSection)
              ) : (
                renderSectionContent(activeSection)
              )}
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={saving} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading || !hasChanges() || allTabsHidden}
          style={{ minWidth: 100 }}
        >
          {saving ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChannelSettingsDialog;
